import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import React, { useEffect, useState } from 'react';
import restaurantStore, {
  restaurantAtom,
  selectRestaurantShowAtom,
} from '~/store/restaurant';
import { fetchRestaurants } from './api';
import storage from '~/storage';
import { RestaurantItem } from '~/types/restaurant';
import { Button, Select } from 'antd';
import Header from '~/App/components/Header';
import NoRestaurant from './NoRestaurant';

const useRestaurantItems = () => {
  const { data } = useSuspenseQuery({
    queryKey: ['restaurants'],
    queryFn: async () => {
      const { restaurants } = await fetchRestaurants();
      return restaurants ?? [];
    },
    staleTime: Infinity,
    gcTime: Infinity,
  });
  return data;
};

const SelectRestaurantModal = ({
  initialValue,
  onSelect,
  onCancel,
}: {
  initialValue: RestaurantItem | null;
  onSelect: (item: RestaurantItem) => void;
  onCancel: () => void;
}) => {
  const items = useRestaurantItems();
  const [value, setValue] = useState(initialValue?.restaurantId);

  const options = items.map(({ restaurantId, name }) => ({
    value: restaurantId,
    label: name,
  }));

  const onSubmit = () => {
    if (!value) return null;
    onSelect(items.find(({ restaurantId }) => restaurantId === value)!);
  };

  const hideButtons = Boolean(initialValue == null);

  return (
    <div className="w-full h-screen flex flex-col bg-white fixed z-50">
      <Header
        title="Pilih Restoran"
        hideBackButton={hideButtons}
        hideHomeButton
        onBack={onCancel}
      />
      <div className="w-full flex-1 flex flex-col justify-center items-center px-6">
        <div className="w-full max-w-[800px] flex flex-col gap-6">
          <Select
            options={options}
            value={value}
            onChange={setValue}
            size="large"
            placeholder="Pilih restoran"
          />
          <Button
            type="primary"
            onClick={onSubmit}
            disabled={!value}
            size="large"
          >
            Submit
          </Button>
        </div>
      </div>
    </div>
  );
};

const RestaurantProvider = () => {
  const restaurantItems = useRestaurantItems();

  const [showModal, setShowModal] = useAtom(selectRestaurantShowAtom);
  const [restaurant, _setRestaurant] = useAtom(restaurantAtom);

  const queryClient = useQueryClient();

  const saveRestaurant = (item: RestaurantItem) => {
    restaurantStore.setRestaurantId(item.restaurantId);
    storage.set('restaurantId', item.restaurantId);
    _setRestaurant(item);
  };

  useEffect(() => {
    (() => {
      const localStorageValue = storage.get('restaurantId', null);
      if (localStorageValue != null) {
        const match = restaurantItems.find(
          ({ restaurantId }) => restaurantId === localStorageValue
        );
        if (match) {
          saveRestaurant(match);
          return;
        } else {
          storage.remove('restaurantId');
        }
      }
      setShowModal(true);
    })();
  }, [restaurantItems]);

  const onChangeModal = (value: RestaurantItem) => {
    saveRestaurant(value);
    setShowModal(false);
    queryClient.clear();
    window.location.reload();
  };

  if (!restaurantItems?.length) {
    return <NoRestaurant />;
  }

  return showModal ? (
    <SelectRestaurantModal
      initialValue={restaurant}
      onSelect={onChangeModal}
      onCancel={() => setShowModal(false)}
    />
  ) : null;
};

export default RestaurantProvider;
